import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type SvcService, useSetServiceKillSwitch } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { normalizeReason, REASON_MAX, reasonProblem, SERVICE_LABEL } from "@/lib/services-gateway";

/** Which service's kill switch the dialog flips, and which way. */
export interface KillSwitchTarget {
  service: SvcService;
  /** Stop the service for every project, or let it run again. */
  suspend: boolean;
  /** How many projects have a key for the service; `null` when the registry did not say. */
  projects: number | null;
  /** How many gateway projects call the service without the gateway (a stop misses them). */
  offGatewayProjects: number;
}

/**
 * The kill switch: stop one service for every gateway project of this
 * instance, or let it run again. Stopping takes a reason and the service's
 * name typed out; nothing is revoked, so resuming brings every key back.
 */
export function KillSwitchDialog({
  target,
  onOpenChange,
}: {
  target: KillSwitchTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {target && (
          <KillSwitchForm
            key={`${target.service}-${target.suspend}`}
            target={target}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function KillSwitchForm({ target, onClose }: { target: KillSwitchTarget; onClose: () => void }) {
  const killSwitch = useSetServiceKillSwitch();
  const [text, setText] = useState("");
  const [typed, setTyped] = useState("");
  const reason = normalizeReason(text);
  const label = SERVICE_LABEL[target.service];
  const problem = target.suspend || reason ? reasonProblem(reason) : null;
  const confirmed = !target.suspend || typed.trim() === target.service;
  const canSubmit = problem === null && confirmed && !killSwitch.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    try {
      const result = await killSwitch.mutateAsync({
        service: target.service,
        suspended: target.suspend,
        reason: reason || null,
        confirm: target.suspend ? typed.trim() : null,
      });
      const done = target.suspend ? "stopped for every project" : "running again";
      toast.success(
        result?.changed === false
          ? `${label} was already ${done}.`
          : `${label} ${done}. It takes effect within about a minute.`,
      );
      const missed = target.suspend ? (result?.off_gateway_projects ?? 0) : 0;
      if (missed > 0) {
        toast.warning(
          `${missed} project${missed === 1 ? " calls" : "s call"} ${label} without the gateway and ${missed === 1 ? "is" : "are"} not stopped.`,
        );
      }
      onClose();
    } catch (err) {
      logger.error("[ai-services] kill switch failed:", err);
      // A toast, not a line in the form: the dialog may have been closed meanwhile.
      toast.error(readApiErrorMessage(err, "Could not change the kill switch. Please try again."));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {target.suspend ? `Stop ${label} for every project?` : `Let ${label} run again?`}
        </DialogTitle>
        <DialogDescription>
          {target.suspend
            ? `Every gateway project's app${target.projects === null ? "" : ` (${target.projects} with a key for it)`} has its calls refused within about a minute, until an admin resumes the service here. No key is revoked; owners see your reason.`
            : "Every project's keys for this service work again within about a minute, except where a project or the service of one project is suspended on its own."}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          {target.suspend && target.offGatewayProjects > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {target.offGatewayProjects} project
              {target.offGatewayProjects === 1 ? " calls" : "s call"} {label} without the gateway (
              {target.service === "llm" ? "models direct" : "their own Langfuse"}) and{" "}
              {target.offGatewayProjects === 1 ? "is" : "are"} not stopped by this.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="kill-switch-reason">
              {target.suspend ? "Reason (every owner reads it)" : "Reason (optional, logged)"}
            </Label>
            <Textarea
              id="kill-switch-reason"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={REASON_MAX + 100}
              rows={3}
              placeholder={target.suspend ? "e.g. Foundry quota incident" : undefined}
            />
            {text.trim() !== "" && problem && (
              <p className="text-muted-foreground text-xs">{problem}</p>
            )}
          </div>
          {target.suspend && (
            <div className="space-y-1.5">
              <Label htmlFor="kill-switch-confirm">
                Type <code className="bg-muted rounded px-1 font-mono">{target.service}</code> to
                confirm
              </Label>
              <Input
                id="kill-switch-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={killSwitch.isPending}>
          Cancel
        </Button>
        <Button
          variant={target.suspend ? "destructive" : "default"}
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {killSwitch.isPending
            ? "Working…"
            : target.suspend
              ? "Stop for every project"
              : "Run again"}
        </Button>
      </DialogFooter>
    </>
  );
}
